import { defineStore } from 'pinia'

import { ApplicationConfigService } from '~/libs/store-services/application-config-service'
import { ApplicationExperienceService } from '~/libs/store-services/application-experience-service'
import appNameFromRoute from '~/libs/app-name-from-route'

import { useBorrowerStore } from '~/store/borrower'
import { useRootStore } from '~/store/root'
import { useAffiliateCustomizationStore } from '~/store/affiliate-customization'
import { useExperimentsStore } from '~/store/experiments'
import env from '../libs/env'
import { useAppAnalyticsStore } from '~/store/app-analytics'
import { useContactGuestStore } from '~/store/contact-guest'

const defaultExperienceArgs = {
  entrypointIdentifier: null,
  moduleIdentifier: null,
  direction: null,
};

export const useApplicationConfigStore = defineStore('applicationConfig', () => {
  const { $axios, $axiosWithRetry, $applicationConstants } = useNuxtApp()
  const { experienceBaseRoute, singleConfigBaseRoute, eventOptions } = $applicationConstants
  const route = useRoute()
  const applicationConfigService = new ApplicationConfigService($axios, $axiosWithRetry)
  const applicationExperienceService = new ApplicationExperienceService($axios, $axiosWithRetry)
  const borrowerStore = useBorrowerStore()
  const rootStore = useRootStore()
  const affiliateCustomizationStore = useAffiliateCustomizationStore()
  const experimentsStore = useExperimentsStore()
  const appAnalyticsStore = useAppAnalyticsStore()
  const contactGuestStore = useContactGuestStore()
  const appReviewTestEnabled = ref(null)

  /*
  ███████ ████████  █████  ████████ ███████
  ██         ██    ██   ██    ██    ██
  ███████    ██    ███████    ██    █████
       ██    ██    ██   ██    ██    ██
  ███████    ██    ██   ██    ██    ███████
  STATE
  */

  const applicationConfig = ref(null)
  const documentUploadRequestApplicationConfig = ref(null)
  const stale = ref(true)
  const preloadedPages = ref([])
  const preloadedIdentifier = ref('')
  const isBackEvent = ref(false)
  const isBankStatementVerificationUiTestEnabled = ref(false)
  const isTaxReturnVerificationUiTestEnabled = ref(false)
  const completedModuleEventPaths = ref([])
  const navigation = ref(null)
  const submitSections = ref(null)


  /*
   ██████  ███████ ████████ ████████ ███████ ██████  ███████
  ██       ██         ██       ██    ██      ██   ██ ██
  ██   ███ █████      ██       ██    █████   ██████  ███████
  ██    ██ ██         ██       ██    ██      ██   ██      ██
   ██████  ███████    ██       ██    ███████ ██   ██ ███████
  GETTERS
  */

  const appIdentifierRoute = computed(() => {
    // TODO: Update for new route structure
    return `${affiliateCustomizationStore.preapprovalOnly ? 'preapproval' : 'marketplace'}-app`
  })

  const navItems = computed(() => {
    return navigation.value ?? []
  })

  const currentModule = computed(() => {
    return applicationConfig.value?.module ?? null;
  })

  const currentModuleIdentifier = computed(() => {
    return currentModule.value?.identifier ?? null;
  })

  const borrowerApplicationType = computed(() => {
    return applicationConfig.value?.borrowerApplicationType ?? null;
  })

  /*
   █████   ██████ ████████ ██  ██████  ███    ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ████   ██ ██
  ███████ ██         ██    ██ ██    ██ ██ ██  ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ██  ██ ██      ██
  ██   ██  ██████    ██    ██  ██████  ██   ████ ███████
  ACTIONS
  ! - - Actions calling other actions in the same store must use `this.actionName(...)`
  ! - - If we do not use `this.actionName` it will not be properly mockable in tests.
  ! - - Computeds and refs will work fine, and should be called directly though.
  */

  async function setIsBackEvent(isBack = false) {
    isBackEvent.value = isBack
  }

  function _setConfigValues({ _applicationConfig, _navigation, _submitSections, identifier }) {
    if (!_applicationConfig.modules && _applicationConfig.module) {
      _applicationConfig.modules = [_applicationConfig.module]
    }

    // update applicationConfig state
    applicationConfig.value = _applicationConfig
    submitSections.value = _submitSections
    navigation.value = _buildNavigation(_navigation)

    stale.value = false

    // cache values
    preloadedIdentifier.value = identifier
    preloadedPages.value = _applicationConfig.modules.reduce((pageSlugs, module) => {
      if (module.includesData) {
        pageSlugs.push(module.urlSlug)
      }
      return pageSlugs
    }, [])

    return applicationConfig.value
  }

  function _setAppExperienceValues({ _applicationConfig, _navigation, _submitSections }) {
    applicationConfig.value = _applicationConfig
    submitSections.value = _submitSections
    navigation.value = _buildNavigation(_navigation)

    stale.value = false

    return applicationConfig.value
  }

  function _buildNavigation(navigation) {
    if (!navigation || !navigation.length) {
      return []
    }

    return navigation.reduce((acc, curr) => {
      // build nexted navigation sections if navLabelSection is defined
      if (curr?.navSectionLabel) {
        const section = acc.find(navItem => navItem.label === curr.navSectionLabel);
        if (section) {
          section.visibleChildren.push(curr)
          section.visited = section.visited || curr.visited;
          return acc;
        }

        acc.push({
          label: curr.navSectionLabel,
          type: 'section',
          url: curr.url,
          visited: curr.visited,
          visibleChildren: [curr],
          hiddenChildren: [],
        })
        return acc
      }

      acc.push(curr)
      return acc;
    }, [])
  }

  /**
   * @param resumeAppStrategy {string}  default | resumeProgress | resumeCompletedProgress | resumeLoanAppForced
   * @returns {Promise<{resumeUrl: *}>}
   */
  async function getResumeAppExperience(resumeAppStrategy= 'default') {
    const borrower = await borrowerStore.getBorrower()
    const borrowerId = borrower?.id
    const tenantId = affiliateCustomizationStore.tenantId
    const allowedResumeStrategies = ['default', 'resumeSbaApp', 'resumeProgress', 'resumeCompletedProgress', 'resumeLoanAppForced']
    const type = allowedResumeStrategies.includes(resumeAppStrategy) ? resumeAppStrategy : 'default'

    if (import.meta.client) {
      appAnalyticsStore.fsTrackEvent({name: 'Resume App Experience', properties: { type }})
    }

    console.log(`Resume app experience strategy: ${type}`)

    if (!borrowerId || !tenantId) {
      throw createError({
        statusCode: 'borrowerId and tenantId are required to fetch resume app experience',
        statusMessage: 400,
        fatal: true
      })
    }

    const { data, error } = await applicationConfigService.getResumeAppExperience({ borrowerId, tenantId, type })

    const resumeUrl = data?.resumeUrl
    const resource = data?.applicationExperienceResource
    const identifier = data?.identifier

    let errorMsg = 'Failed to fetch resume app experience'
    let errorStatus = 400

    if (error) {
      switch (error.status) {
        case 403:
          errorStatus = 403
          errorMsg = `${errorMsg} Access denied`
          break
        case 404:
          errorStatus = 404
          errorMsg = `${errorMsg} Page not found`
          break
        default:
          errorStatus = 500
          errorMsg = `${errorMsg} ${error.message}`
      }
    }

    if (error || !resumeUrl) {
      throw createError({
        statusCode: errorStatus,
        statusMessage: errorMsg,
        fatal: true
      })
    }

    if (resource && identifier && resource?.applicationConfig && resource?.navigation && resource?.submitSections) {
      _setConfigValues({
        _applicationConfig: resource.applicationConfig,
        _navigation: resource.navigation,
        _submitSections: resource.submitSections,
        identifier
      });
    }

    return { resumeUrl }
  }

  async function getApplicationConfigData({entrypointIdentifier = null, moduleIdentifier = null, direction = null} = defaultExperienceArgs) {
    const loadedModuleIdentifier = applicationConfig.value?.module?.identifier

    if (moduleIdentifier && moduleIdentifier === loadedModuleIdentifier && !direction && !stale.value) {
      return applicationConfig.value
    }

    const borrower = await borrowerStore.getBorrower()
    const borrowerId = borrower?.id
    const tenantId = affiliateCustomizationStore.tenantId
    const anonymousId = rootStore.getAnonymousId()

    if (!borrowerId && !anonymousId) {
      const errorMsg =`Failed to fetch ${entrypointIdentifier ? 'Application Config by Entrypoint' : 'Application Experience' }`
      log.warning(errorMsg, { message: 'No borrowerId or anonymousId', moduleIdentifier })
      return applicationConfig.value
    }

    const payload = {
      anonymousId,
      borrowerId,
      tenantId,
      entrypointIdentifier,
      moduleIdentifier,
      direction,
    }

    const { data, error } = await applicationExperienceService.getApplicationConfigData(payload)

    if (error) {
      // TODO: Update for new route structure
      const routePrefix = entrypointIdentifier ? `${singleConfigBaseRoute}/${entrypointIdentifier}` : experienceBaseRoute
      return _configFetchErrorHandler(error, `${routePrefix}/${moduleIdentifier}`)
    }

    const _applicationConfig = data?.applicationConfig
    const _navigation = data?.navigation
    const _submitSections = data?.submitSections

    return _setAppExperienceValues({ _applicationConfig, _navigation, _submitSections })
  }

  async function getDocumentUploadRequestApplicationConfig(documentUploadRequestInviteToken = null) {
    const borrowerId = borrowerStore.borrower?.id;

    const response = route.path.includes('/document-upload-request/contact-guest')
    ? await $axios.get(`${env('apiUrl')}/contact/guest/additional-owners/post-offer-documents`, {
      headers: { 'Authorization': `lendio-jwt ${contactGuestStore.token}` }
    })
    : await $axios.get(
      borrowerId
        ? `${env('apiUrl')}/application-config/borrower/${borrowerId}/document-upload-request`
        : `${env('apiUrl')}/application-config/guest/document-upload-request`,
      {
        headers: !borrowerId && documentUploadRequestInviteToken ? {
          Authorization: `lendio-jwt ${documentUploadRequestInviteToken}`,
        } : null,
        params: { tenantId: affiliateCustomizationStore.tenantId }
      }
    )

    const configFromResponse = response?.data?.data ?? null

    if (configFromResponse) {
      configFromResponse.modules = configFromResponse?.modules?.map((module) => ({ ...module, completed: documentModuleComplete(module)}));
    }

    documentUploadRequestApplicationConfig.value = configFromResponse
    return documentUploadRequestApplicationConfig.value
  }

  function documentModuleComplete(module) {
    return module.moduleDocuments.every(moduleDocument => moduleDocument.requirements.every(req => req.document))
  }

  async function postApplicationExperienceEvent({ moduleIdentifier, eventType, entrypointIdentifier = null, path = null }) {
    if (!moduleIdentifier || !eventType) {
      return false
    }
    if (eventType === eventOptions.moduleCompleted && path && !checkPathForCompletedEvent(path)) {
      completedModuleEventPaths.value.push(path)
    }
    const borrower = await borrowerStore.getBorrower()
    const borrowerId = borrower?.id
    const anonymousId = rootStore.getAnonymousId()

    if (!borrowerId && !anonymousId) {
      log.warning('Failed to save application experience event', { message: 'No borrowerId or anonymousId', eventType })
      return false
    }

    const payload = {
      moduleIdentifier,
      entrypointIdentifier,
      eventType,
      anonymousId,
      borrowerId,
      tenantId: affiliateCustomizationStore.tenantId
    }

    const { error } = await applicationConfigService.postApplicationExperienceEvent(payload)

    if (error) {
      log.warning('Failed to save application experience event', { message: error.message, eventType })
    }

    // Return boolean on success. No error = success
    return !error
  }

  function loadAnswersFromStorage() {
    if (borrowerStore.borrower || borrowerStore.borrowerId) {
      return {}
    }
    return applicationConfigService.loadAnswersFromStorage()
  }

  async function saveAnswers({ answers: _answers = null } = {}) {
    if (!Object.keys(_answers).length) {
      return
    }

    if (!borrowerStore.borrower || !borrowerStore.borrowerId) {
      const data = _answers
      applicationConfigService.mergeAnswersToStorage(data)

      return Object.keys(_answers)
    }

    const data = await applicationConfigService.saveAnswers({
      authenticated: Boolean(rootStore.authUser),
      answers: _answers,
      borrowerId: borrowerStore.borrowerId
    })
    stale.value = true

    if (data) {
      return Object.keys(_answers)
    }

    return []
  }

  async function updateApplicationReviewEnabled(){
    await _updateExperimentEnabledStoreVar({
      experimentState: appReviewTestEnabled,
      experimentDetails:[
        {
          name: 'Review Page',
          allowedAppNames: ['marketplace']
        },
        {
          name: 'IL - Review Page',
          allowedAppNames: ['il']
        },
        {
          name: 'EM - Review Page',
          allowedAppNames: ['embedded']
        }
      ]
    })
  }

  async function updateBankStatementVerificationTestEnabled() {
    if (isBankStatementVerificationUiTestEnabled.value === true) return;

    await _updateExperimentEnabledStoreVar({
      experimentState: isBankStatementVerificationUiTestEnabled,
      experimentDetails: [{name: 'Bank Statement Verification UI Test', allowedAppNames: ['marketplace']}],
    })
  }

 async function updateTaxReturnVerificationTestEnabled() {
    if (isTaxReturnVerificationUiTestEnabled.value === true) return;

    await _updateExperimentEnabledStoreVar({
      experimentState: isTaxReturnVerificationUiTestEnabled,
      experimentDetails: [{name: 'Tax Return Verification UI Test', allowedAppNames: ['marketplace']}],
    })
  }

  /**
   * experimentState is a ref() to track if experiment is enabled
   * experimentDetails contains one more experiments e.g. [{name: 'Experiment Name', allowedAppNames: ['marketplace', 'embedded']}]
   */
  async function _updateExperimentEnabledStoreVar({experimentState, experimentDetails = [], testGroup = 1} = {}) {
    if (!experimentState || experimentState?.value === undefined || !experimentDetails.length) {
      return
    }

    const user = rootStore.authUser
    if (!user) {
      experimentState.value = false
      return
    }

    const appName = appNameFromRoute(route)
    if (!appName) {
      experimentState.value = false
      return
    }

    const filteredExperimentNames = experimentDetails.reduce((returnArray, experiment) => {
      if (experiment.allowedAppNames.includes(appName)) {
        returnArray.push(experiment.name)
      }
      return returnArray
    }, [])
    if (!filteredExperimentNames.length) {
      experimentState.value = false
      return
    }

    await experimentsStore.fetchActiveExperimentsForCurrentUser()
    experimentState.value = experimentsStore.activeUserExperiments
      .some((userExperiment) => filteredExperimentNames.includes(userExperiment.name) && userExperiment.group === testGroup)
  }

  async function _updateAnonymousExperimentEnabledStoreVar({experimentState, experimentDetails = [], testGroup = 1} = {}) {
    if (!experimentState || experimentState?.value === undefined || !experimentDetails.length) {
      return
    }

    const appName = appNameFromRoute(route)
    if (!appName) {
      experimentState.value = false
      return
    }

    const filteredExperimentNames = experimentDetails.reduce((returnArray, experiment) => {
      if (experiment.allowedAppNames.includes(appName)) {
        returnArray.push(experiment.name)
      }
      return returnArray
    }, [])
    if (!filteredExperimentNames.length) {
      experimentState.value = false
      return
    }

    experimentState.value = experimentsStore.anonymousExperiments
      .some((anonExperiment) => {
        return filteredExperimentNames.includes(anonExperiment?.experiment?.name) && anonExperiment.group === testGroup
      })
  }

  function checkPathForCompletedEvent(path = null) {
    if (!path) {
      return false
    }
    return completedModuleEventPaths.value.includes(path)
  }

  function setConfigStale(staleValue) {
    stale.value = staleValue
  }

  function _configFetchErrorHandler(error, routeName) {
    let errorStatus
    let errorMsg = `Failed to fetch Application data for ${routeName}`
    switch (error.status) {
      case 403:
        errorStatus = 403
        errorMsg = `${errorMsg} Access denied: ${routeName}`
        break
      case 404:
        errorStatus = 404
        errorMsg = `${errorMsg} Page not found: ${routeName}`
        break
      default:
        errorStatus = 500
        errorMsg = `${errorMsg} ${error.message}`
    }
    throw createError({
      statusCode: errorStatus,
      statusMessage: errorMsg,
      fatal: true
    })
  }

  return {
    // STATE
    applicationConfig,
    appReviewTestEnabled,
    completedModuleEventPaths,
    documentUploadRequestApplicationConfig,
    isBackEvent,
    isBankStatementVerificationUiTestEnabled,
    isTaxReturnVerificationUiTestEnabled,
    navigation,
    navItems,
    preloadedIdentifier,
    preloadedPages,
    stale,
    submitSections,

    // GETTERS
    appIdentifierRoute,
    borrowerApplicationType,
    currentModule,
    currentModuleIdentifier,

    // ACTIONS
    checkPathForCompletedEvent,
    getApplicationConfigData,
    getDocumentUploadRequestApplicationConfig,
    loadAnswersFromStorage,
    postApplicationExperienceEvent,
    saveAnswers,
    setConfigStale,
    setIsBackEvent,
    getResumeAppExperience,
    updateApplicationReviewEnabled,
    updateBankStatementVerificationTestEnabled,
    updateTaxReturnVerificationTestEnabled,
  }
})
